import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  UserPlus,
  Search,
  Edit,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import UserPlusIcon from "@/components/icons/UserPlusIcon";
import EditIcon from "@/components/icons/EditIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";

const UsersIndexPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, user: null });
  const [isSearching, setIsSearching] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Fetch available roles for filtering with user counts
  const { data: rolesData, error: rolesError } = useQuery(
    ["roles-with-counts"],
    async () => {
      const response = await api.get("/roles/with-counts");
      return response.data;
    },
    {
      onError: (error) => {
        toast.error("Failed to load roles for filtering");
      },
    }
  );

  const { data, isLoading, error } = useQuery(
    ["users", page, debouncedSearch, roleFilter, statusFilter],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { is_active: statusFilter }),
      });
      const response = await api.get(`/users?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: "always",
    }
  );

  // Reset page to 1 when search or filters change
  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const deleteMutation = useMutation((userId) => api.delete(`/users/${userId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast.success("User deleted successfully");
      setDeleteDialog({ isOpen: false, user: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete user");
    },
  });

  const handleDeleteClick = (user) => {
    setDeleteDialog({
      isOpen: true,
      user: user,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.user) {
      deleteMutation.mutate(deleteDialog.user.id);
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "destructive";
      case "field engineer":
        return "default";
      case "peer reviewer":
        return "secondary";
      case "site reviewer":
        return "outline";
      case "director reviewer":
        return "warning";
      case "client":
        return "success";
      case "user":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeVariant = (isActive) => {
    return isActive ? "success" : "secondary";
  };

  // Get unique roles from current data for quick filtering
  const availableRoles = rolesData?.data || [];

  if (error) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <p className="text-red-600">Failed to load users</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="slide-up">
      <div className="">
        {/* Header */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="slide-up">
            <Link to="/users/create">
              <Button className="w-full shadow-lg hover:bg-red-900 hover:shadow-xl sm:w-auto">
                <UserPlusIcon className="w-5 h-5 mr-2" />
                Add User
              </Button>
            </Link>
          </div>
          {/* Filters */}
          <div className="flex flex-col gap-3 md:gap-4">
            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 lg:flex lg:flex-row lg:items-end">
              <div className="min-w-[220px] flex-1">
                <div className="relative">
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="border-primary/20 focus:border-primary/40 pl-9"
                    aria-label="Search users"
                  />
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 pointer-events-none left-3 top-1/2 text-gray-0" />
                </div>
              </div>
              <div className="relative z-30 w-full md:w-full lg:w-48">
                <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent className="z-50 text-gray-400 shadow-lg bg-navy-100">
                    <SelectItem value="">All Roles</SelectItem>
                    {availableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.name}>
                        {role.name} {role.user_count > 0 && `(${role.user_count})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative z-30 w-full md:w-full lg:w-48">
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status">
                      {statusFilter === "true"
                        ? "Active"
                        : statusFilter === "false"
                          ? "Inactive"
                          : statusFilter === ""
                            ? "All Status"
                            : "Filter by status"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-50 text-gray-400 shadow-lg bg-navy-100">
                    <SelectItem value="">All Status</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleClearFilters} className="w-full hover:bg-red-900 sm:w-auto">
                  <CloseIcon className="w-5 h-5 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="shadow-sm backdrop-blur-sm">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200 hover:bg-gray-50/50">
                {/* <TableHead>Name</TableHead> */}
                <TableHead className="sticky md:sticky md:left-0 md:top-0">Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-center">Status</TableHead>
                {/* <TableHead>Created</TableHead>
                <TableHead>Last Login</TableHead> */}
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isSearching ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center">
                    <div className="text-center">
                      <LoadingSpinner size="lg" />
                      <p className="mt-4 text-gray-600">Searching users...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-b-2 rounded-full border-primary animate-spin"></div>
                      <span className="ml-3 text-gray-600">Loading users...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : data?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center">
                    <div className="text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No users found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((user, index) => (
                  <TableRow key={user.id} className={`animate-stagger-${(index % 5) + 1}`}>
                    {/* <TableCell className="font-medium">
                      <div className="p-4 my-2 mr-2 rounded-t-lg bg-navy-100 ">
                          <div className="flex items-center gap-2 font-medium text-gray-0">
                            {user.first_name} {user.last_name}
                            {user.is_super_admin && (
                              <span className="text-yellow-500">★</span>
                            )}
                          </div>
                      </div>
                    </TableCell> */}
                    <TableCell className="sticky text-gray-600 max-w-80 md:sticky md:left-0 md:top-0">
                      <div className="p-3 my-2 mr-2 rounded-t-lg bg-navy-100 text-gray-0 sm:p-4">
                        <div className="text-sm break-words truncate whitespace-nowrap sm:text-base">
                          {user.email}
                          {user.is_super_admin && <span className="ml-2 text-yellow-500">★</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-80 ">
                      <div className="p-3 m-2 rounded-t-lg max-w-300 bg-navy-100 text-gray-0 sm:p-4">
                        {user.is_super_admin ? (
                          <Badge className="text-xs text-red-600 border-none rounded-none sm:text-sm">
                            Super Admin
                          </Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1 truncate max-w-70 whitespace-nowrap">
                            {user.role && user.role !== "No Role" ? (
                              user.role.split(",...").map((roleName, index, array) => (
                                <span key={index} className="flex items-center truncate whitespace-nowrap">
                                  <Badge className="p-1 text-xs border-none rounded-none">
                                    {roleName.trim()}
                                  </Badge>
                                  {index < array.length - 1 && (
                                    <span className="text-gray-0">,</span>
                                  )}
                                </span>
                              ))
                            ) : (
                              <Badge variant="secondary" className="text-xs rounded-full scroll-pl-6">
                                No Role
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="p-3 m-2 text-center uppercase rounded-t-lg bg-navy-100 text-gray-0 sm:p-4">
                        <Badge
                          className={`border-none p-0 text-xs sm:text-sm ${user.is_active ? "text-green-500" : "text-red-600"}`}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </TableCell>
                    {/* <TableCell className="text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : "Never"}
                    </TableCell> */}
                    <TableCell className="w-4 text-center">
                      {!user.is_super_admin && (
                        <div className="p-2 my-2 ml-2 text-center rounded-t-lg bg-navy-100 text-gray-0 whitespace-nowrap">
                          <Link to={`/users/${user.id}/edit`}>
                            <Button variant="outline" size="sm" className="rounded-lg">
                              <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(user)}
                            className="hover:text-red-600"
                            disabled={user.usage_count > 0}
                          >
                            <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                          </Button>
                        </div>
                      )}
                      {user.is_super_admin && (
                        <div className="p-3 my-2 ml-2 text-center rounded-t-lg select-none bg-navy-100 text-gray-0 sm:p-4">
                          <Badge className="text-xs text-green-500 border-none rounded-none sm:text-sm ">
                            Protected
                          </Badge>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {data?.pagination && (
            <>
              <div className="flex flex-col gap-4 pr-0 border-t border-b border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
                <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
                  <span className="block sm:inline">
                    Showing {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                    {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)}{" "}
                    of {data.pagination.total} users
                  </span>
                </div>
                <div className="flex items-center justify-center w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1 text-red-500 sm:mr-2" />
                    <span className="hidden sm:inline">PREVIOUS</span>
                    <span className="sm:hidden">PREV</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                  >
                    {page}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.pages}
                  >
                    <span className="hidden sm:inline">NEXT</span>
                    <span className="sm:hidden">NEXT</span>
                    <ChevronRight className="w-4 h-4 ml-1 text-red-500 sm:ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, user: null })}
          onConfirm={handleDeleteConfirm}
          userName={
            deleteDialog.user
              ? `${deleteDialog.user.first_name} ${deleteDialog.user.last_name}`
              : ""
          }
          title="Delete User"
          isLoading={deleteMutation.isLoading}
        />
      </div>
    </div>
  );
};

export default UsersIndexPage;
